import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';
import { GenderAgeDisplay, MaskedEmailDisplay, TalkStatusBadge, NameNicknameDisplay, ExternalLinkButton } from '../common';
import { 
  Nurse, 
  ROLE_COLORS, 
  ROLE_TYPES, 
  WORKSHOP_STATUS_COLORS, 
  WORKSHOP_STATUS_TEXT_COLORS, 
  WORKSHOP_STATUS_TYPES,
  NURSE_TYPE_THEME 
} from '../../types/nurse';
import { getExternalWorkshopRoomUrl } from '../../utils/urlUtils';
import { formatDateWithTime, getHumanReadableTimeAgo } from '../../utils/dateUtils';

interface NurseTableRowProps {
  nurse: Nurse;
  trainingMode: boolean;
}

/**
 * 看護師一覧の各行を表示するコンポーネント
 */
const NurseTableRow: React.FC<NurseTableRowProps> = ({nurse, trainingMode}) => {
  const navigate = useNavigate();

  // ロール名を日本語に変換
  const getRoleLabel = (roleType: string) => {
    return ROLE_TYPES[roleType as keyof typeof ROLE_TYPES] || roleType;
  };

  return (
    <TableRow
      hover
      onClick={(event) => {
        // 特定の子要素がクリックされた場合はイベント伝播をスキップし、詳細に遷移しない
        if ((event.target as HTMLElement).closest('.no-propagation')) {
          return;
        }
        navigate(`/nurses/${nurse.id}`);
      }}
      sx={theme => ({
        cursor: 'pointer', // クリック可能なことを示すカーソル
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        ...(nurse.resigned_at ? {
          backgroundColor: '#fcf5f5', // 退会済みユーザーはごく薄い赤に
          boxShadow: 'inset 0 0 5px rgba(220, 220, 220, 0.5)', // 少し影を付ける
          opacity: 0.8, // 少し透明に
          '& td': {color: '#777'}, // テキストは少し薄く
          borderLeft: '3px solid #ffcccc', // 左側に目印の線
        } : {})
      })}
    >
      {/* 対話状態/研修ステータス列 */}
      <TableCell>
        {trainingMode ? (
          /* 研修看護師モード: 研修ステータスを表示（四角形、塗りつぶしなし） */
          <Box
            sx={{
              display: 'inline-block',
              border: `1px solid ${nurse.workshop_status ?
                WORKSHOP_STATUS_COLORS[nurse.workshop_status as keyof typeof WORKSHOP_STATUS_COLORS] || '#1976d2' :
                '#bdbdbd'}`,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '1rem',
              textAlign: 'center',
              minWidth: 90,
              fontWeight: 'bold'
            }}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 'bold',
                color: nurse.workshop_status ?
                  WORKSHOP_STATUS_TEXT_COLORS[nurse.workshop_status as keyof typeof WORKSHOP_STATUS_TEXT_COLORS] || '#1976d2' :
                  '#757575',
                fontSize: '1.0rem'
              }}
            >
              {nurse.workshop_status ? WORKSHOP_STATUS_TYPES[nurse.workshop_status as keyof typeof WORKSHOP_STATUS_TYPES] || nurse.workshop_status : '未設定'}
            </Typography>
          </Box>
        ) : (
          /* 通常モード: 対話状態を表示（共通コンポーネント） */
          <TalkStatusBadge
            isActive={!!nurse.in_active_talk}
            isResigned={!!nurse.resigned_at}
          />
        )}
      </TableCell>

      {/* 研修修了回数 - 研修看護師モードでのみ表示 */}
      {trainingMode && (
        <TableCell align="center">
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              color: nurse.workshop_finish_count > 0 ? '#2e7d32' : '#9e9e9e',
              fontSize: '1.2rem'
            }}
          >
            {nurse.workshop_finish_count}
          </Typography>
        </TableCell>
      )}

      {/* 最新の研修チャット列 - 研修看護師モードのみ */}
      {trainingMode && (
        <TableCell sx={{minWidth: '180px', width: '180px'}}>
          {nurse.latest_workshop_room && (
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              {nurse.latest_workshop_room.id ? (
                <ExternalLinkButton
                  href={getExternalWorkshopRoomUrl(nurse.latest_workshop_room.id)}
                  label={`${nurse.latest_workshop_room.workshop_count || 0}回目`}
                  tooltip="最新の研修ルームを開く"
                  tooltipPlacement="top"
                  size="small"
                  variant="outlined"
                  buttonType="chat"
                  themeColor={{
                    main: NURSE_TYPE_THEME.TRAINING.DARK,
                    hover: `${NURSE_TYPE_THEME.TRAINING.BACKGROUND}99`
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{mb: 0.5}}
                  className="no-propagation"
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{color: 'text.secondary', fontSize: '0.85rem'}}
                >
                  研修記録なし
                </Typography>
              )}
              {nurse.latest_workshop_room.last_chat_send_at && (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5}}>
                  <Tooltip
                    title={`最終チャット日時: ${formatDateWithTime(nurse.latest_workshop_room.last_chat_send_at)}`}
                    arrow
                  >
                    <Box sx={{display: 'flex', alignItems: 'center', cursor: 'help'}}>
                      <Typography variant="caption" sx={{color: 'text.secondary', fontSize: '0.75rem', mr: 0.5}}>
                        最終: {getHumanReadableTimeAgo(nurse.latest_workshop_room.last_chat_send_at)}
                      </Typography>
                      <AccessTimeIcon fontSize="small" color="action" sx={{fontSize: '0.9rem', opacity: 0.7}}/>
                    </Box>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
        </TableCell>
      )}

      {/* ロール列 */}
      <TableCell>
        <Chip
          label={getRoleLabel(nurse.role_type)}
          size="small"
          style={{
            backgroundColor: ROLE_COLORS[nurse.role_type as keyof typeof ROLE_COLORS] || '#757575',
            color: 'white',
            fontWeight: 'bold',
            opacity: nurse.resigned_at ? 0.6 : 1 // 退会済みはロール表示も少し薄く
          }}
        />
      </TableCell>

      {/* 名前・ニックネーム列 */}
      <TableCell>
        <NameNicknameDisplay
          name={nurse.name}
          nickname={nurse.nickname}
          isTestAccount={nurse.is_test_account}
          nameColor={nurse.resigned_at ? '#666' : undefined}
          nicknameColor={nurse.resigned_at ? '#888' : 'text.secondary'}
        />
      </TableCell>

      {/* 性別・年齢列 */}
      <TableCell align="left" sx={{textAlign: 'left'}}>
        <GenderAgeDisplay genderType={nurse.gender_type} birthday={nurse.birthday}/>
      </TableCell>

      {/* 受付状態 - 通常モードのみ */}
      {!trainingMode && (
        <TableCell align="center">
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <Typography
              component="span"
              sx={{
                color: nurse.can_assign ? '#2e7d32' : '#c62828',
                fontWeight: 'bold',
                fontSize: '1.2rem',
                mr: 0.5,
                ...(nurse.resigned_at ? {opacity: 0.6} : {})
              }}
            >
              {nurse.can_assign ? '○' : '×'}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: nurse.can_assign ? '#2e7d32' : '#c62828',
                fontSize: '0.9rem',
                ...(nurse.resigned_at ? {opacity: 0.6} : {})
              }}
            >
              {nurse.can_assign ? '受付中' : '停止中'}
            </Typography>
          </Box>
        </TableCell>
      )}

      {/* メールアドレス列 */}
      <TableCell>
        <MaskedEmailDisplay email={nurse.email} isBold={false}/>
      </TableCell>
    </TableRow>
  );
};

export default NurseTableRow;
